// Application tray store — holds the member's multi-program selection
// across the Mode 1 home, program detail, and Plan tab. Persisted to
// localStorage so selections survive refresh (per spec §3 carry-forward).
//
// Mid-2026 update — cross-device persistence
// ------------------------------------------
// localStorage was originally chosen for simplicity but it's per-browser.
// A member picking programs on iPhone Safari then signing in on a
// laptop would land with an empty tray (different browser, different
// store). To bridge those gaps we now ALSO sync to the backend:
//
//   • A stable anonymous device id (cookie `vy9_anon_tray_id`,
//     generated client-side, 90-day max-age) keys an anonymous tray
//     row in the BE. So even pre-sign-in, a member can pick on phone
//     and the laptop tray will rehydrate when the cookie syncs (or
//     more practically: when they sign in on the laptop, the BE
//     claims the anon tray onto their account).
//   • Once signed in, the account's tray takes precedence; the anon
//     tray is claimed + merged on first sign-in event.
//
// All BE calls are best-effort: when the endpoints return 404 (BE
// hasn't shipped yet) we silently fall back to local-only behavior,
// matching the playbook-acknowledge pattern we already use.
//
// BE contract (handoff for the BE agent):
//   GET  /api/consumer/tray             → { items: [...] }   (auth required)
//   PUT  /api/consumer/tray             { items: [...] }
//   POST /api/consumer/tray/claim       { anonId: "..." }    (merges anon
//        into account tray, returns merged items)
//   GET  /api/consumer/tray/anon/{id}   → { items: [...] }   (no auth)
//   PUT  /api/consumer/tray/anon/{id}   { items: [...] }     (no auth)
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, getToken, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ApplicationTrayCtx = createContext(null);
// SERVER IS THE ONLY SOURCE OF TRUTH FOR TRAY ITEMS.
//
// We deliberately do NOT cache tray items in localStorage. Earlier
// versions tried to keep an offline mirror keyed per-consumer, but
// effect-ordering races allowed an anonymous tray to leak into a
// freshly-registered account's cache during the auth transition
// (mirror effect writing to the new user's key BEFORE the
// identity-change handler had a chance to reset state). Architecting
// the tray as server-of-record eliminates that entire class of bugs.
//
// The anonymous-tray cookie (`vy9_anon_tray_id`) still exists so that
// a guest visitor can add programs while not signed in and have the
// BE persist that selection by anon ID. On first sign-in within the
// SAME tab session — gated by the sessionStorage `vy9_anon_claim_ok`
// flag — we POST /consumer/tray/claim to graft the anon row onto the
// fresh account. Any leftover cookie WITHOUT the in-session flag is
// considered stale (prior visitor, family computer, deleted account)
// and is disowned outright.
const ANON_COOKIE = "vy9_anon_tray_id";
const ANON_COOKIE_MAX_AGE = 60 * 60 * 24 * 90; // 90 days
const REMOTE_DEBOUNCE_MS = 400;

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[-.+*]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}
function writeCookie(name, value, maxAge) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}
// sessionStorage flag set the moment a new anon cookie is minted in
// this tab. The cookie alone is not enough to authorise a claim into
// an account tray — it survives logout, account deletion, and other
// users sharing the browser. The flag below ties the cookie to THIS
// tab's anonymous session, so claim only fires when the cookie was
// actually populated by the current visitor. After any claim (or
// account switch / logout) the flag is cleared.
const ANON_CLAIM_FLAG = "vy9_anon_claim_ok";
function setAnonClaimFlag() {
  try { window.sessionStorage.setItem(ANON_CLAIM_FLAG, "1"); } catch { /* private mode */ }
}
function hasAnonClaimFlag() {
  try { return window.sessionStorage.getItem(ANON_CLAIM_FLAG) === "1"; } catch { return false; }
}
function clearAnonClaimFlag() {
  try { window.sessionStorage.removeItem(ANON_CLAIM_FLAG); } catch { /* private mode */ }
}

function ensureAnonId() {
  let id = readCookie(ANON_COOKIE);
  if (!id) {
    // Browser-supplied randomUUID() when available, fallback for older Safari.
    id = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    writeCookie(ANON_COOKIE, id, ANON_COOKIE_MAX_AGE);
    // Mark this cookie as "ours" — minted in this tab during this
    // anon session. Used to authorise claim on subsequent sign-in.
    setAnonClaimFlag();
  }
  return id;
}

// Whether the current page has an authenticated consumer session.
// We use the api lib's getToken() so we stay aligned with the auth
// storage convention — keeps the tray provider independent of mount
// order while reading the real JWT key.
function hasConsumerAuth() {
  try { return Boolean(getToken()); } catch { return false; }
}

// Dedupe by programId, preferring the more recently-mutated entry.
// `updatedAt` lives on each row (we add it whenever the tray is
// mutated locally) so the BE merge can do the same on its side.
//
// We support two item kinds in the same tray (per Research feature
// rollout, Feb 2026):
//   - kind === "program"  → dedup key: programId
//   - kind === "research" → dedup key: sku + variant
// Legacy program items without `kind` are treated as program.
function dedupeKey(item) {
  if (!item) return null;
  if (item.kind === "research" && item.sku) {
    return `research::${item.sku}::${item.variant || "subscription"}`;
  }
  if (item.programId) return `program::${item.programId}`;
  return null;
}
function mergeItems(a, b) {
  const map = new Map();
  [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])].forEach((item) => {
    const key = dedupeKey(item);
    if (!key) return;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, item);
      return;
    }
    const ta = new Date(existing.updatedAt || 0).getTime();
    const tb = new Date(item.updatedAt || 0).getTime();
    if (tb >= ta) map.set(key, item);
  });
  return Array.from(map.values());
}

export function ApplicationTrayProvider({ children }) {
  // items: [{ programId, programSlug, programName, brandColor,
  //           tierId, tierKey, tierName, monthlyPrice, updatedAt }]
  //
  // Always starts empty. The server is the single source of truth —
  // mount hydration immediately pulls from /consumer/tray (or the
  // anon row), and the identity-change effect re-pulls on every
  // sign-in / sign-out. We never cache items in localStorage.
  const [items, setItems] = useState([]);
  // Track which "scope" the in-memory items belong to so we can decide
  // whether to push to the anon row or the account row on every write.
  // `null` while hydration is in flight; "anon" or "account" once
  // resolved.
  const scopeRef = useRef(null);
  const debounceRef = useRef(null);
  const hydratedRef = useRef(false);

  // Subscribe to AuthContext so the tray reacts immediately when the
  // member signs in / out. We use the consumer identity (id) as the
  // trigger so a profile refresh that returns the same consumer
  // doesn't kick a redundant pull.
  const { consumer } = useAuth();
  const consumerKey = consumer?.id || consumer?._id || (consumer ? "present" : null);
  const lastConsumerKeyRef = useRef(consumerKey);

  // Stable snapshot of items so visibility/unload handlers always see
  // the latest selection without re-binding the listeners on every
  // mutation (which would otherwise cause a flush on tab return too).
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // ---- One-time migration cleanup.
  //
  // Earlier versions of this provider mirrored tray items into
  // localStorage under `voya9.applicationTray.v1` (anon) and
  // `voya9.applicationTray.v1:<consumerId>` (per-user). Tray data is
  // now server-only — these keys would otherwise sit around forever
  // and could be read by any future regression. We wipe them on
  // every mount; cheap, idempotent, and bullet-proof against the
  // cross-account leak that was caused by the old cache.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const toRemove = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith("voya9.applicationTray.v1")) toRemove.push(k);
      }
      toRemove.forEach((k) => window.localStorage.removeItem(k));
    } catch { /* private mode — nothing we can do */ }
  }, []);

  // ---- Server hydration on mount + auth flips
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const authed = hasConsumerAuth();
      // Phase 1: signed-in hydration.
      //
      // CRITICAL ORDERING: we ALWAYS read the account tray first, then
      // only claim the anon tray when it actually has items. Otherwise
      // a guest cookie on a fresh browser would trigger a claim that —
      // depending on BE merge semantics — could wipe a populated
      // account tray (this was the cross-device "Browser B shows empty
      // tray" bug). After any claim we re-read the account tray as the
      // final source of truth.
      let serverItems = null;
      try {
        if (authed) {
          // 1a) Read the account tray. This is the source of truth.
          try {
            const res = await apiGet("/consumer/tray");
            serverItems = Array.isArray(res?.items) ? res.items : null;
            // eslint-disable-next-line no-console
            console.info("[tray] account read", { count: serverItems?.length ?? 0 });
          } catch (err) {
            if (err?.status && err.status !== 404) {
              // eslint-disable-next-line no-console
              console.warn("[tray] account read failed", err);
            }
          }

          // 1b) If we have a guest cookie AND it was minted in this
          // tab session (vy9_anon_claim_ok in sessionStorage), check
          // whether the anon tray is populated and claim it onto the
          // account. Without the sessionStorage flag the cookie is
          // leftover from a prior visitor / session / deleted account
          // and MUST NOT be claimed — that path is exactly the
          // cross-account leak we are guarding against on shared /
          // family computers.
          const anonId = readCookie(ANON_COOKIE);
          const claimAuthorised = anonId && hasAnonClaimFlag();
          if (anonId && !claimAuthorised) {
            // Disown leftover cookie outright — there is no legitimate
            // reason to consult it once this tab is authed under an
            // identity that didn't mint the cookie.
            writeCookie(ANON_COOKIE, "", 0);
            clearAnonClaimFlag();
          }
          if (claimAuthorised) {
            let anonItems = null;
            try {
              const res = await apiGet(`/consumer/tray/anon/${encodeURIComponent(anonId)}`);
              anonItems = Array.isArray(res?.items) ? res.items : null;
            } catch (err) {
              if (err?.status && err.status !== 404 && err.status !== 400) {
                // eslint-disable-next-line no-console
                console.warn("[tray] anon read (pre-claim) failed", err);
              }
            }
            if (anonItems && anonItems.length > 0) {
              try {
                const claimed = await apiPost("/consumer/tray/claim", { anonId });
                // Trust BE merged response but re-read the account tray
                // immediately after to guard against any merge drift.
                const claimedItems = Array.isArray(claimed?.items) ? claimed.items : null;
                if (claimedItems && claimedItems.length > 0) serverItems = claimedItems;
                writeCookie(ANON_COOKIE, "", 0);
                clearAnonClaimFlag();
                try {
                  const verify = await apiGet("/consumer/tray");
                  if (Array.isArray(verify?.items) && verify.items.length > 0) {
                    serverItems = verify.items;
                  }
                  // eslint-disable-next-line no-console
                  console.info("[tray] post-claim verify", { count: verify?.items?.length ?? 0 });
                } catch { /* keep claimed result */ }
              } catch (err) {
                if (err?.status && err.status !== 404) {
                  // eslint-disable-next-line no-console
                  console.warn("[tray] claim failed", err);
                }
              }
            } else {
              // Anon tray was empty — clear the cookie so we don't
              // re-attempt this dance on every page nav.
              writeCookie(ANON_COOKIE, "", 0);
              clearAnonClaimFlag();
            }
          }
          scopeRef.current = "account";
        } else {
          // Phase 2: anonymous mode. Hit the anon row.
          const anonId = ensureAnonId();
          try {
            const res = await apiGet(`/consumer/tray/anon/${encodeURIComponent(anonId)}`);
            serverItems = Array.isArray(res?.items) ? res.items : null;
          } catch (err) {
            if (err?.status && err.status !== 404) {
              // eslint-disable-next-line no-console
              console.warn("[tray] anon read failed", err);
            }
          }
          scopeRef.current = "anon";
        }
      } catch {
        /* network failure — `items` stays empty until next pull. */
      }

      if (cancelled) return;
      hydratedRef.current = true;
      // Merge so any mutation that happened DURING hydration (e.g. user
      // tapped Add on a marketing page while the server fetch was in
      // flight) is preserved. mergeItems prefers the newer updatedAt,
      // so server wins ties and any optimistic local addition stays.
      if (serverItems && serverItems.length > 0) {
        setItems((prev) => mergeItems(prev, serverItems));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- Lightweight server pull. Used by the visibility/focus
  // listeners + the identity-change effect so the tab silently picks
  // up changes made by the member on another device — no UI button,
  // no toast, just a quiet merge.
  //
  // SELF-HEALING SCOPE: we re-check auth on every call. If the tab
  // hydrated in anon mode (e.g., because localStorage hadn't restored
  // the JWT yet when the provider mounted) but the member has since
  // signed in, we silently upgrade to account scope and read the
  // account tray — and vice-versa on logout.
  //
  // CRITICAL: pullFromServer does NOT claim the anon tray. Claim is
  // a one-shot lifecycle event reserved for the initial provider
  // mount only. Calling claim here would, on a shared/family browser,
  // graft the anon row from a previous user's session onto whoever
  // signs in next — exactly the cross-account leak we are guarding
  // against. The identity-change effect explicitly clears the anon
  // cookie before pulling so the anon row never participates in
  // post-mount sync.
  const pullFromServer = useCallback(async () => {
    if (!hydratedRef.current) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;

    const authedNow = hasConsumerAuth();
    const desiredScope = authedNow ? "account" : "anon";
    if (scopeRef.current !== desiredScope) {
      // eslint-disable-next-line no-console
      console.info("[tray] scope corrected", { from: scopeRef.current, to: desiredScope });
      scopeRef.current = desiredScope;
    }

    try {
      let serverItems = null;
      if (scopeRef.current === "account") {
        const res = await apiGet("/consumer/tray");
        serverItems = Array.isArray(res?.items) ? res.items : null;
      } else {
        const anonId = readCookie(ANON_COOKIE);
        if (!anonId) return;
        const res = await apiGet(`/consumer/tray/anon/${encodeURIComponent(anonId)}`);
        serverItems = Array.isArray(res?.items) ? res.items : null;
      }
      if (!serverItems) return;
      // eslint-disable-next-line no-console
      console.info("[tray] background pull", { scope: scopeRef.current, count: serverItems.length });
      setItems((prev) => {
        const merged = mergeItems(prev, serverItems);
        // Propagate cross-device REMOVALS too — but only when there's
        // no pending local PUT in flight that hasn't reached the
        // server yet (otherwise we'd undo an in-progress edit).
        if (debounceRef.current) return merged;
        const serverIds = new Set(serverItems.map((it) => it.programId));
        const localOnly = prev.filter((it) => !serverIds.has(it.programId));
        const newestServer = serverItems.reduce(
          (max, it) => Math.max(max, new Date(it.updatedAt || 0).getTime()),
          0,
        );
        const survivors = localOnly.filter((it) => {
          const t = new Date(it.updatedAt || 0).getTime();
          return t > newestServer;
        });
        return mergeItems(survivors, serverItems);
      });
    } catch (err) {
      if (err?.status && err.status !== 404 && err.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("[tray] background pull failed", err);
      }
    }
  }, []);

  // ---- Flush pending PUT immediately (skips debounce). Used on
  // tab close / page hide so we never lose a selection the member made
  // moments before they navigated away. Uses `fetch` with
  // `keepalive: true` directly — apiPut's fetch doesn't set keepalive
  // and a regular request is killed when the tab unloads.
  const flushPendingSync = useCallback((itemsSnapshot) => {
    if (!hydratedRef.current) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const payload = JSON.stringify({ items: itemsSnapshot });
    try {
      const token = getToken();
      let url;
      if (scopeRef.current === "account") {
        url = `${API_BASE}/consumer/tray`;
      } else {
        const anonId = ensureAnonId();
        url = `${API_BASE}/consumer/tray/anon/${encodeURIComponent(anonId)}`;
      }
      // eslint-disable-next-line no-console
      console.info("[tray] flush PUT (keepalive)", { scope: scopeRef.current, count: itemsSnapshot.length });
      fetch(url, {
        method: "PUT",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: payload,
      }).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  // Bind tab-lifecycle listeners ONCE. The listeners read the latest
  // items via `itemsRef` so they don't need to re-bind on every
  // mutation (which would otherwise flush on tab return — incorrect).
  useEffect(() => {
    const onPageHide = () => flushPendingSync(itemsRef.current);
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPendingSync(itemsRef.current);
      } else if (document.visibilityState === "visible") {
        pullFromServer();
      }
    };
    const onFocus = () => pullFromServer();
    const onStorage = (e) => {
      // Token written/cleared in another tab (or same-tab via a
      // different module) — re-evaluate so the tray scope tracks the
      // real auth state without waiting for the next focus event.
      if (e.key === "voya9_token") pullFromServer();
    };
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, [flushPendingSync, pullFromServer]);

  // ---- Auth-state-driven self-heal.
  //
  // The single most important sync trigger: whenever the AuthContext
  // `consumer` identity changes (sign-in completes, profile loaded,
  // sign-out, account switch), we (1) cancel any in-flight PUT so it
  // can't carry the previous user's items to the new user's server
  // tray, (2) swap the in-memory items to the per-user localStorage
  // cache (empty for a fresh user — guarantees no cross-member leak
  // on a shared/family computer), (3) kill the anon cookie so any
  // stale anon-tray row from a prior session can't pollute the new
  // identity via a subsequent claim or anon-read, then (4) silently
  // pull the new user's server tray to populate the UI.
  //
  // CRITICAL: we do NOT call /consumer/tray/claim here. Claim is
  // strictly a one-shot lifecycle event reserved for the initial
  // provider mount (i.e., the legitimate anon-browse → first-sign-in
  // flow). After mount, identity changes are treated as account
  // switches and the anon tray is disowned outright.
  useEffect(() => {
    if (lastConsumerKeyRef.current === consumerKey) return;
    const prevKey = lastConsumerKeyRef.current;
    lastConsumerKeyRef.current = consumerKey;
    // eslint-disable-next-line no-console
    console.info("[tray] auth state change", { from: prevKey, to: consumerKey });

    // (1) Kill any pending debounced PUT. It would target the OLD
    // user's scope/credentials with the OLD user's items — never
    // appropriate after an identity change.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // (2) Hard-reset in-memory items. The new identity starts from a
    // blank slate — server pull (step 5) is the source of truth. This
    // is what stops member A's items from appearing in member B's
    // tray on a family / shared computer. No localStorage involved.
    setItems([]);

    // (3) Kill the anon cookie + its claim flag. The
    // vy9_anon_tray_id cookie's only legitimate purpose is to bridge
    // an anonymous browse session into the first sign-in (handled by
    // initial-mount claim, gated by the sessionStorage flag set
    // when the cookie was minted). After that — and on every account
    // switch — both are stale state that belongs to nobody. Clearing
    // them here means a subsequent anon read can't surface a previous
    // user's items, and a subsequent reload + sign-in cannot claim
    // them either.
    writeCookie(ANON_COOKIE, "", 0);
    clearAnonClaimFlag();

    // (4) Reset scope so pullFromServer rebinds it from scratch
    // (anon ↔ account) and re-fetches authoritative state.
    scopeRef.current = null;
    pullFromServer();
  }, [consumerKey, pullFromServer]);

  // ---- Debounced push of local changes to the server
  const scheduleRemoteSync = useCallback((nextItems) => {
    if (!hydratedRef.current) return; // don't push pre-hydration garbage
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      const payload = { items: nextItems };
      try {
        if (scopeRef.current === "account") {
          // eslint-disable-next-line no-console
          console.info("[tray] PUT account", { count: nextItems.length });
          await apiPut("/consumer/tray", payload);
        } else {
          const anonId = ensureAnonId();
          await apiPut(`/consumer/tray/anon/${encodeURIComponent(anonId)}`, payload);
        }
      } catch (err) {
        // Best-effort — 404 means BE isn't shipped yet, localStorage
        // continues to carry us. Anything else, log for visibility.
        if (err?.status && err.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn("[tray] remote sync failed", err);
        }
      }
    }, REMOTE_DEBOUNCE_MS);
  }, []);

  const addOrReplace = useCallback((item) => {
    setItems((prev) => {
      // Only one tier per program — replace if they pick a different tier.
      const filtered = prev.filter((p) => p.programId !== item.programId);
      const stamped = { ...item, kind: "program", updatedAt: new Date().toISOString() };
      const next = [...filtered, stamped];
      scheduleRemoteSync(next);
      return next;
    });
  }, [scheduleRemoteSync]);

  const remove = useCallback((programId) => {
    setItems((prev) => {
      const next = prev.filter((p) => p.programId !== programId);
      scheduleRemoteSync(next);
      return next;
    });
  }, [scheduleRemoteSync]);

  // Research-item mutators. Dedup key is (sku, variant) — picking a
  // different variant for the same SKU replaces the previous row.
  // Quantity defaults to 1.
  const addOrReplaceResearch = useCallback((item) => {
    setItems((prev) => {
      const variant = item.variant || "subscription";
      const filtered = prev.filter(
        (p) => !(p.kind === "research" && p.sku === item.sku && (p.variant || "subscription") === variant),
      );
      const stamped = {
        ...item,
        kind: "research",
        variant,
        quantity: Math.max(1, Number(item.quantity) || 1),
        updatedAt: new Date().toISOString(),
      };
      const next = [...filtered, stamped];
      scheduleRemoteSync(next);
      return next;
    });
  }, [scheduleRemoteSync]);

  const removeResearch = useCallback((sku, variant) => {
    setItems((prev) => {
      const v = variant || "subscription";
      const next = prev.filter(
        (p) => !(p.kind === "research" && p.sku === sku && (p.variant || "subscription") === v),
      );
      scheduleRemoteSync(next);
      return next;
    });
  }, [scheduleRemoteSync]);

  const clear = useCallback(() => {
    setItems([]);
    scheduleRemoteSync([]);
  }, [scheduleRemoteSync]);

  const value = useMemo(
    () => ({
      items,
      addOrReplace,
      remove,
      addOrReplaceResearch,
      removeResearch,
      clear,
      count: items.length,
      programItems: items.filter((it) => (it.kind || "program") === "program"),
      researchItems: items.filter((it) => it.kind === "research"),
    }),
    [items, addOrReplace, remove, addOrReplaceResearch, removeResearch, clear],
  );

  return <ApplicationTrayCtx.Provider value={value}>{children}</ApplicationTrayCtx.Provider>;
}

export function useApplicationTray() {
  const ctx = useContext(ApplicationTrayCtx);
  if (!ctx) throw new Error("useApplicationTray must be used inside ApplicationTrayProvider");
  return ctx;
}
